/**
 * Payment API module.
 */

import { apiClient } from "@/lib/api/client";
import { isNativeMobilePlatform } from "@/lib/utils/ua";
import type {
  SourceDetails,
  PaymentMethod,
  BalanceResponse,
  Invoice,
  AutoReloadConfig,
  UserSubscription,
  PaddleTransaction,
  StripeCharge,
  PaymentSourceType,
  CreateSubscriptionData,
  Offer,
} from "./types";

// -- Payment Sources ------------------------------------------------

export async function listSources(
  paymentSource: PaymentSourceType = "stripe" as PaymentSourceType,
) {
  return apiClient.get<PaymentMethod[]>(
    `/payment/listSources?paymentSource=${paymentSource}`,
  );
}

export async function updateSource(data: {
  sourceId: string;
  isDefault?: boolean;
}) {
  return apiClient.post<{ status: string }>("/payment/updateSource", data);
}

export async function deleteSource(data: {
  paymentSource: PaymentSourceType;
  sourceId?: string;
}) {
  return apiClient.post<{ status: string }>("/payment/deleteSource", data);
}

// -- Balance --------------------------------------------------------

export async function getBalance() {
  return apiClient.get<BalanceResponse>("/payment/balance");
}

export async function addBalanceToAccount(data: { paymentIntentId: string }) {
  // Only the PaymentIntent id is sent — the server re-fetches it from Stripe and credits the
  // verified `amount_received`. No client-supplied amount is trusted. Server returns `balance`.
  return apiClient.post<{ balance: number | string }>(
    "/payment/addBalanceToAccount",
    data,
  );
}

export async function createChargeByBalance(data: SourceDetails) {
  return apiClient.post<{ status: string }>(
    "/payment/createChargeByBalance",
    data,
  );
}

// -- Stripe ---------------------------------------------------------

export async function createPaymentIntent(data: SourceDetails) {
  // `id` is the PaymentIntent id — echoed back to addBalanceToAccount after client confirmation.
  return apiClient.post<{ id: string; clientSecret: string }>(
    "/payment/paymentIntent",
    data,
  );
}

/**
 * Wallet top-up PaymentIntent. Distinct from createPaymentIntent (uploads) because the server
 * stamps an immutable purpose='wallet_topup' — the only intent addBalanceToAccount will credit.
 */
export async function createTopupIntent(data: SourceDetails) {
  return apiClient.post<{ id: string; clientSecret: string }>(
    "/payment/topupIntent",
    data,
  );
}

export async function getSetupIntent() {
  return apiClient.get<{ clientSecret: string }>("/payment/createSetupIntent");
}

export async function saveSetupIntent(data: {
  setupIntentId: string;
  paymentMethodId?: string;
}) {
  return apiClient.post<{ status: string }>("/payment/saveSetupIntent", data);
}

// -- Paddle ---------------------------------------------------------

export async function addPaddlePayment() {
  return apiClient.post<{ transactionId: string }>("/payment/addPaddle");
}

export async function createChargeByPaddle(data: {
  amount: number;
  description: string;
}) {
  // Server charges Paddle AND credits the wallet from that same amount, returning the new balance.
  return apiClient.post<{ balance: number | string }>(
    "/payment/createChargeByPaddle",
    data,
  );
}

// -- Coupons --------------------------------------------------------

/**
 * Verify a coupon code. Server returns the Stripe coupon shape:
 *  - isValid: boolean
 *  - couponCodeId: the Stripe coupon id passed to subscription creation
 *  - percentOff: percent discount (0 if amount-based coupon)
 *  - amountOff: amount discount in CENTS (0 if percent-based) — caller must divide by 100 for dollars
 *  - message: server-provided error reason when isValid === false
 */
export async function verifyCouponCode(data: { code: string }) {
  return apiClient.post<{
    isValid: boolean;
    couponCodeId?: string;
    percentOff?: number;
    amountOff?: number;
    message?: string;
  }>("/payment/verifyCouponCode", data);
}

// -- Auto-Reload ----------------------------------------------------

export async function getAutoReloadStatus() {
  return apiClient.get<AutoReloadConfig>("/payment/autoReload");
}

export async function updateAutoReloadStatus(data: AutoReloadConfig) {
  return apiClient.post<{ status: string }>("/payment/autoReload", data);
}

// -- Subscription ---------------------------------------------------

export async function getUserSubscription() {
  return apiClient.get<UserSubscription>("/subscription");
}

export async function getFileSizeUsage() {
  return apiClient.get<{ fileSize: number }>(
    "/subscription/quota?type=fileSize",
  );
}

export async function payAsYouGoPlan() {
  return apiClient.post<{ status: string }>("/subscription/payAsYouGo", {});
}

/**
 * POST /subscription/stripe — server destructures plan fields flat from req.body
 * (see speak-server/src/@speak-payment/controller/subscriptionController.ts:204).
 * `paymentMethodId` is the Stripe payment source id; server reads it as `sourceId`.
 * Send the full plan flat with `sourceId` set, NOT wrapped in `{planData}`.
 */
export async function stripePlan(data: {
  paymentMethodId: string;
  planData: CreateSubscriptionData;
}) {
  return apiClient.post<{
    status: string;
    requiresAction?: boolean;
    clientSecret?: string;
  }>(
    "/subscription/stripe",
    { ...data.planData, sourceId: data.paymentMethodId },
  );
}

/**
 * POST /subscription/revenuecat — server destructures the same plan fields flat
 * as Stripe AND a nested `revenuecat: { customerId, productIdentifier, transactionId, storeType }`
 * (see speak-server/src/@speak-payment/controller/subscriptionController.ts:573).
 * It also reads `req.body.revenuecat.isActive` to gate sandbox/non-active receipts.
 * Send the full plan flat, with the nested revenuecat object and sourceType.
 */
export async function revenuecatPlan(data: {
  planData: CreateSubscriptionData;
  revenuecat: {
    customerId: string;
    productIdentifier: string;
    transactionId: string;
    storeType: string;
    isActive: boolean;
  };
}) {
  return apiClient.post<{ status: string }>("/subscription/revenuecat", {
    ...data.planData,
    revenuecat: data.revenuecat,
    sourceType: "revenuecat",
  });
}

/**
 * POST /subscription/paddle — server destructures the same plan fields flat as Stripe
 * (subscriptionController.createPaddlePlan). Send full plan flat; transactionId is
 * server-issued for the Paddle checkout iframe and returned in the response.
 * Angular parity: initial call sends no transactionId; server returns one when
 * a Paddle checkout overlay is required.
 */
export async function paddlePlan(data: {
  transactionId?: string;
  planData: CreateSubscriptionData;
}) {
  return apiClient.post<{ status: string; transactionId?: string }>(
    "/subscription/paddle",
    {
      ...data.planData,
      ...(data.transactionId ? { transactionId: data.transactionId } : {}),
    },
  );
}

export async function cancelPlan(data: {
  companyId: string;
  reason?: string;
  questions?: { question: string; answer: string }[];
  isOfferAccepted?: boolean;
  offer?: { label: string; offer: string }[];
  feedback?: string;
}) {
  return apiClient.post<{ status: string }>("/subscription/cancel", data);
}

export async function acceptOfferPlan(data: {
  companyId: string;
  reason?: string;
  offer?: { label: string; offer: string }[];
  feedback?: string;
}) {
  return apiClient.post<{ status: string }>("/subscription/cancel", {
    ...data,
    isOfferAccepted: true,
  });
}

export async function pausePlan(data: {
  companyId: string;
  duration: number;
  reason?: string;
  questions?: { question: string; answer: string }[];
}) {
  return apiClient.post<{ status: string }>("/subscription/pause", data);
}

export async function resumePlan(data: { companyId: string }) {
  return apiClient.post<{ status: string }>("/subscription/resume", data);
}

export async function getUsageAnalytics() {
  return apiClient.get<{
    media: { media: number; hours: number };
    recorder: { recorders: number; recordings: number };
    meetingAssistants: { calendars: number; meetingAssistants: number };
    chat: { totalChat: number };
    automations: number;
  }>("/analytics/usage");
}

export async function getSubscriptionIntent() {
  return apiClient.get<{ clientSecret: string }>("/subscription/intent");
}

export async function getPaddleTransaction() {
  // The transactions endpoint also returns paid/completed history, so keep only the
  // payable ones (ready/billed) and take the most recent.
  const res = await getTransactions("paddle" as PaymentSourceType);
  const list = res.success ? ((res.data ?? []) as PaddleTransaction[]) : [];
  const latest = list
    .filter((t) => t.status === "ready" || t.status === "billed")
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  return { ...res, data: latest };
}

// -- Offers --------------------------------------------------------

export function getOffers() {
  const isMobile = isNativeMobilePlatform();
  return apiClient.get<Offer[]>(`/offers/me?isMobile=${isMobile}`);
}

export function getSpecialOffers() {
  return apiClient.get<Offer[]>(`/offers/me?special=true`);
}

/**
 * Fetch eligible offers for a company resolved from a signed link token.
 * Public endpoint — no auth. The token carries the companyId; the server
 * returns the same `Offer[]` shape as `/offers/me?special=true`.
 */
export function getOffersByToken(token: string) {
  return apiClient.get<Offer[]>(
    `/offers/by-token?token=${encodeURIComponent(token)}`,
  );
}

/**
 * Claim an offer via on-the-fly hosted checkout. Resolves the company from the
 * session (logged-in) or the `token` (public link), reserves the redemption,
 * and returns the provider-hosted checkout URL to redirect to. `provider`
 * selects the rail: `stripe` = pay by card, `paddle` = pay with PayPal.
 */
export function claimOffer(data: {
  offerId: string;
  provider: "stripe" | "paddle";
  token?: string;
}) {
  return apiClient.post<{ url: string }>("/offers/claim", data);
}

// -- Invoices & Transactions ----------------------------------------

export async function getTransactions(
  paymentSource: PaymentSourceType = "stripe" as PaymentSourceType,
) {
  return apiClient.get<unknown[]>(
    `/subscription/transactions?paymentSource=${paymentSource}`,
  );
}

export async function getInvoices(
  paymentSource: PaymentSourceType = "stripe" as PaymentSourceType,
) {
  return apiClient.get<Invoice[]>(
    `/subscription/invoices?paymentSource=${paymentSource}`,
  );
}

export async function getUpcomingInvoice(
  paymentSource: PaymentSourceType = "stripe" as PaymentSourceType,
) {
  return apiClient.get<Invoice>(
    `/subscription/invoice/upcoming?paymentSource=${paymentSource}`,
  );
}

export async function downloadInvoice(data: {
  paymentSource: PaymentSourceType;
  transaction: Invoice | StripeCharge | PaddleTransaction;
}) {
  return apiClient.postBlob("/subscription/invoice/download", data);
}
